import React, { useCallback, useEffect, useState } from 'react';
import {
    BackButton,
    Controls,
    FormBody,
    FormBox,
    GroupInputs,
    StyledButton,
    StyledCheckbox,
    Title,
    TitleText,
    Wrapper,
} from './register-step.styles';
import { ArrowsArrowLeft } from '@vtbhome/juicyfront/indexIcon';
import { RegisterFormFields, RegisterStepProps } from './types';
import { RegisterFormKeys } from '../../config/form-register-constants';
import { Field, Form } from 'react-final-form';
import { useCompanyOptions } from '../../lib/hooks/use-company-options/use-company-options';
import { convertFormFields } from '../../lib/utils/form-register-convert-fields';
import { validate } from '../../lib/utils/form-register-validate';
import { debounce } from '@shared/lib/utils/debounce/debounce';
import { InputText } from '@shared/ui/input-text/input-text';
import { TEXT_MASK_PATTERN } from '@shared/ui/input-text/constants';
import { Select } from '@shared/ui/select/select';
import { InputPhone } from '@shared/ui/input-phone/input-phone';
import { Typography } from '@shared/ui/typography/typography';
import { useRegisterMutation } from '../../api/register-api';
import { getOrganizationByInn } from '../../lib/utils/get-organization-by-inn';
import { useSaveSuggestions } from '../../lib/hooks/use-save-suggestions/use-save-suggestions';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useLazyOrganizationSuggestionQuery } from '@entities/organization/api/organization-api';
import { RegisterPolicy } from '../register-policy/register-policy';
import { UserProfileOptionPublicDataRequest } from '@entities/user-profile-option/api/user-profile-option-types';
import { DEFAULT_USER_PROFILE_OPTIONS_PUBLIC_LIST_PARAMETERS } from '@entities/user-profile-option/config/store-constants';
import { useUserProfileOptionsPublicInfinityListQuery } from '@entities/user-profile-option/api/user-profile-option-api';
import { useDepartmentSelect } from '@entities/user-profile-option/libs/hooks/use-department-options/use-department-options';
import { useCurrentUserUtm } from '@entities/current-user/libs/hooks/use-current-user-utm/use-current-user-utm';

export const RegisterStep: React.FC<RegisterStepProps> = ({ isShow, onPrevStep, onNextStep }) => {
    const [getOrganization, getOrganizationResult] = useLazyOrganizationSuggestionQuery();
    const [registerUser, registerUserResult] = useRegisterMutation();
    const utmData = useCurrentUserUtm();
    const getOrganizationsList = useCallback(debounce(getOrganization, 200), [getOrganization]);
    const suggestions = getOrganizationResult.data?.items || [];
    const allUniqSuggestions = useSaveSuggestions(suggestions);
    const companyOptions = useCompanyOptions({
        suggestions,
        isLoadingSuggestions: getOrganizationResult.isLoading || getOrganizationResult.isFetching,
    });
    const isSendingForm = registerUserResult.isLoading;
    const formError = getQueryError(registerUserResult);

    /* Department */
    const [departmentListParameters, setDepartmentListParameters] = useState<UserProfileOptionPublicDataRequest>({
        ...DEFAULT_USER_PROFILE_OPTIONS_PUBLIC_LIST_PARAMETERS,
        take: 10,
    });
    const {
        data: departmentsList,
        isLoading: isDepartmentsListLoading,
        isFetching: isDepartmentsListFetching,
    } = useUserProfileOptionsPublicInfinityListQuery(departmentListParameters);

    const {
        departmentOptions,
        departmentEmptyOption,
        isShowEmptyLoaderDepartment,
        infinityScrollDepartmentProps,
        onSearchDepartment,
    } = useDepartmentSelect({
        departmentsData: departmentsList,
        isLoading: isDepartmentsListLoading,
        isFetching: isDepartmentsListFetching,
        setDepartmentListParameters,
    });

    const handleSubmit = (fields: RegisterFormFields) => {
        const chosenOrganization = getOrganizationByInn({
            inn: fields['register-company-inn'],
            organizations: allUniqSuggestions,
        });
        if (chosenOrganization) {
            return registerUser(convertFormFields({ fields, organization: chosenOrganization, utmData }));
        }
    };

    useEffect(() => {
        const email = registerUserResult.originalArgs?.email;
        if (registerUserResult.isSuccess && email) {
            onNextStep(email);
        }
    }, [registerUserResult.isSuccess, registerUserResult.originalArgs?.email]);

    if (!isShow) {
        return null;
    }

    return (
        <Wrapper>
            <FormBody>
                <Title>
                    <BackButton buttonType='icon' size='m' onClick={onPrevStep}>
                        <ArrowsArrowLeft />
                    </BackButton>
                    <TitleText size='h4'>Форма регистрации</TitleText>
                </Title>
                <Form
                    onSubmit={handleSubmit}
                    validate={(fields) => validate({ fields, organizations: allUniqSuggestions })}
                    validateOnBlur
                    render={(form) => {
                        return (
                            <FormBox onSubmit={form.handleSubmit} noValidate>
                                <GroupInputs>
                                    <Field
                                        name={RegisterFormKeys.Surname}
                                        render={({ input, meta }) => (
                                            <InputText
                                                {...input}
                                                required
                                                label='Фамилия'
                                                placeholder='Введите фамилию'
                                                error={meta.touched && meta.error}
                                                disabled={isSendingForm}
                                                mask={TEXT_MASK_PATTERN}
                                            />
                                        )}
                                    />

                                    <Field
                                        name={RegisterFormKeys.Name}
                                        render={({ input, meta }) => (
                                            <InputText
                                                {...input}
                                                required
                                                label='Имя'
                                                placeholder='Введите имя'
                                                error={meta.touched && meta.error}
                                                disabled={isSendingForm}
                                                mask={TEXT_MASK_PATTERN}
                                            />
                                        )}
                                    />
                                </GroupInputs>
                                <Field
                                    name={RegisterFormKeys.Patronymic}
                                    render={({ input, meta }) => (
                                        <InputText
                                            {...input}
                                            label='Отчество'
                                            placeholder='Введите отчество'
                                            error={meta.touched && meta.error}
                                            disabled={isSendingForm}
                                            mask={TEXT_MASK_PATTERN}
                                        />
                                    )}
                                />
                                <Field
                                    name={RegisterFormKeys.CompanyINN}
                                    render={({ input, meta }) => (
                                        <Select
                                            {...input}
                                            required
                                            isAsync
                                            label='Компания'
                                            placeholder='Введите название, ИНН или КПП'
                                            variant='base'
                                            error={meta.touched && meta.error}
                                            options={companyOptions}
                                            disabled={isSendingForm}
                                            onSearch={getOrganizationsList}
                                            onChange={(option) => {
                                                input.onChange({ target: { value: option?.[0]?.value } });
                                            }}
                                        />
                                    )}
                                />
                                <Field
                                    name={RegisterFormKeys.JobTitle}
                                    render={({ input, meta }) => (
                                        <InputText
                                            {...input}
                                            required
                                            label='Должность'
                                            placeholder='Введите должность'
                                            error={meta.touched && meta.error}
                                            disabled={isSendingForm}
                                        />
                                    )}
                                />
                                <Field
                                    name={RegisterFormKeys.Department}
                                    render={({ input, meta }) => {
                                        return (
                                            <Select
                                                required
                                                isAsync
                                                clearable
                                                disabled={isSendingForm}
                                                label='Выберите департамент'
                                                placeholder='Выберите департамент из списка'
                                                isShowPreloaderInList={isShowEmptyLoaderDepartment}
                                                onSearch={onSearchDepartment}
                                                infinityScrollProps={infinityScrollDepartmentProps}
                                                values={input.value || departmentEmptyOption}
                                                options={departmentOptions}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        );
                                    }}
                                />
                                <Field
                                    name={RegisterFormKeys.Email}
                                    render={({ input, meta }) => (
                                        <InputText
                                            {...input}
                                            required
                                            label='Корпоративный e-mail'
                                            placeholder='Введите e-mail'
                                            error={meta.touched && meta.error}
                                            disabled={isSendingForm}
                                        />
                                    )}
                                />
                                <Field
                                    defaultValue={'+7'}
                                    name={RegisterFormKeys.Phone}
                                    render={({ input, meta }) => (
                                        <InputPhone
                                            {...input}
                                            required
                                            label='Номер телефона'
                                            error={meta.touched && meta.error}
                                            disabled={isSendingForm}
                                        />
                                    )}
                                />
                                <Field
                                    type='checkbox'
                                    name={RegisterFormKeys.AgreementAdvert}
                                    defaultValue={false}
                                    render={({ input }) => (
                                        <StyledCheckbox
                                            {...input}
                                            label='Согласие на получение рекламных материалов'
                                            value={String(input.value)}
                                            disabled={isSendingForm}
                                        />
                                    )}
                                />
                                <Controls>
                                    <StyledButton
                                        type='submit'
                                        fullWidth
                                        preloader={isSendingForm}
                                        disabled={isSendingForm}>
                                        {!isSendingForm && 'Отправить'}
                                    </StyledButton>
                                    {formError && (
                                        <Typography size='body1_short' color='error_60' align='center'>
                                            Возникла ошибка: {formError.message}
                                        </Typography>
                                    )}
                                </Controls>
                            </FormBox>
                        );
                    }}
                />
            </FormBody>
            <RegisterPolicy />
        </Wrapper>
    );
};
